// @ts-nocheck
import React, { useState, useEffect } from "react";
import { 
  Building2, 
  Users, 
  FileText, 
  Send, 
  LayoutDashboard, 
  ArrowUpRight, 
  ArrowDownRight,
  Clock,
  ShieldCheck,
  PlusCircle,
  ExternalLink,
  PieChart,
  TrendingUp,
  Wallet,
  FileX,
  Calendar,
  X, // 🟢 Ícone para fechar o Slide-Over
  Download // 🟢 Ícone para o botão de CSV
} from "lucide-react";

import { collection, onSnapshot, query, limit, orderBy, collectionGroup } from "firebase/firestore";
import { db } from "../../config/firebase";
import { createPortal } from "react-dom";

// ============================================================================
// COMPONENTES BASE
// ============================================================================
const StatCard = ({ icon: Icon, title, value, colorClass, bgClass, trend = null, alert = null }) => (
  <div className={`bg-white border ${alert ? 'border-red-200/60 bg-red-50/10' : 'border-slate-200/60'} rounded-2xl p-5 sm:p-6 shadow-sm transition-all hover:shadow-md group flex flex-col justify-between relative overflow-hidden`}>
    <div className="flex justify-between items-start mb-4">
      <div className={`p-3 rounded-xl ${bgClass} ${colorClass} transition-transform group-hover:scale-110 duration-300`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex flex-col items-end gap-1">
        {trend && trend.text !== "-" && (
          <span className={`flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-lg ${trend.color} tracking-wide`}>
            {trend.icon} {trend.text}
          </span>
        )}
        {alert && (
          <span className="flex items-center text-[10px] font-bold px-2 py-0.5 rounded-md bg-red-500 text-white animate-pulse tracking-wide">
            {alert}
          </span>
        )}
      </div>
    </div>
    <div>
      <p className="text-xs font-medium text-slate-500 mb-1">
        {title}
      </p>
      <h3 className={`text-2xl sm:text-3xl font-bold ${alert ? 'text-red-600' : 'text-slate-700'}`}>
        {value}
      </h3>
    </div>
  </div>
);

// ============================================================================
// TELA PRINCIPAL DO ADMIN (REAL BENEFÍCIOS)
// ============================================================================
const AdminDashboard = () => {
  const brandColor = "#00a99d";

  const getTodayString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [dateRange, setDateRange] = useState({
    start: getTodayString(),
    end: getTodayString()
  });

  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalUsers: 0,
    pendingInvites: 0,
  });

  // Estados separados para Funil (Requests) e Churn (Contratos)
  const [allRequests, setAllRequests] = useState([]);
  const [allContracts, setAllContracts] = useState([]);

  // 🟢 Novo Estado: Lista consolidada de Imobiliárias e Visibilidade do Slide-Over
  const [allCompanies, setAllCompanies] = useState([]);
  const [isSlideOverOpen, setIsSlideOverOpen] = useState(false);

  const [periodStats, setPeriodStats] = useState({
    fichasCurrent: 0,
    fichasPrev: 0,
    encerradosCurrent: 0,
    encerradosPrev: 0,
    encerramentoPendente: 0,
    aprovadasCurrent: 0,
    volumeAprovado: 0,
    volumeEmAnalise: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);

  // Estados da Saúde da Carteira (Métricas Vitais)
  const [portfolioStats, setPortfolioStats] = useState({
    mrr: 0,
    volumeGarantido: 0,
    taxaInadimplencia: 0,
    contratosEmRisco: 0,
    totalAtivos: 0
  });

  // 🛡️ Parser Resiliente
  const parseCurrency = (val) => {
    if (!val) return 0;
    if (typeof val === 'number') return val;
    const strVal = String(val);
    if (strVal.includes(',')) return parseFloat(strVal.replace(/\./g, '').replace(',', '.')) || 0;
    return parseFloat(strVal) || 0;
  };

  // ============================================================================
  // 1. BUSCA DE DADOS EM TEMPO REAL
  // ============================================================================
  useEffect(() => {
    const unsubscribeCompanies = onSnapshot(collection(db, "companies"), (snapshot) => {
      let membersCount = 0;
      const companiesData = [];
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        membersCount += data.memberUids?.length || 0;
        
        // 🟢 Alimentando a lista para o Slide-Over e CSV
        companiesData.push({
          id: doc.id,
          nome: data.nome || 'Sem nome definido',
          cnpj: data.cnpj || 'Não informado',
          createdAt: data.createdAt?.toDate() || new Date(),
          status: data.status || 'Ativo'
        });
      });

      // Ordena da mais nova para a mais velha
      companiesData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      setAllCompanies(companiesData);
      setStats(prev => ({ ...prev, totalCompanies: snapshot.size, totalUsers: membersCount }));
    });

    const unsubscribeInvites = onSnapshot(collectionGroup(db, "invites"), (snapshot) => {
      setStats(prev => ({ ...prev, pendingInvites: snapshot.size }));
    });

    // Busca as solicitações para o Funil
    const unsubscribeRequests = onSnapshot(query(collection(db, "requests")), (snapshot) => {
      setAllRequests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Busca os contratos para métricas de Churn e Encerrados
    const unsubscribeContracts = onSnapshot(query(collection(db, "contratos_ativos")), (snapshot) => {
      setAllContracts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const qRecent = query(collection(db, "companies"), orderBy("createdAt", "desc"), limit(5));
    const unsubscribeRecent = onSnapshot(qRecent, (snapshot) => {
      const activities = snapshot.docs.map(doc => ({
        id: doc.id,
        title: 'Nova Imobiliária',
        description: doc.data().nome || 'Sem nome definido',
        time: doc.data().createdAt?.toDate()
      }));
      setRecentActivities(activities);
    });

    return () => {
      unsubscribeCompanies();
      unsubscribeInvites();
      unsubscribeRequests();
      unsubscribeContracts();
      unsubscribeRecent();
    };
  }, []);

  // ============================================================================
  // 2. CÁLCULO INTELIGENTE DO DATE PICKER (POR PERÍODO)
  // ============================================================================
  useEffect(() => {
    // Configurando datas com horários limite
    const startD = new Date(`${dateRange.start}T00:00:00`).getTime();
    const endD = new Date(`${dateRange.end}T23:59:59`).getTime();
    const diffTime = Math.abs(endD - startD);
    const prevEndD = startD - 1; 
    const prevStartD = prevEndD - diffTime;

    let fCurrent = 0, fPrev = 0, eCurrent = 0, ePrev = 0, ePend = 0;
    let vAprov = 0, vEmAnalise = 0, aprCurrent = 0;

    // A. MOTOR DE FUNIL (Requests)
    allRequests.forEach((data) => {
      const statusRaw = data.status || "";
      const isApproved = statusRaw === "Aprovada" || statusRaw === "Garantia Ativa";
      const isEvaluating = statusRaw === "Em Analise" || statusRaw === "Análise" || statusRaw === "Pendente";
      
      const creationDate = data.createdAt?.toDate() || data.updatedAt?.toDate();
      const creationTime = creationDate ? creationDate.getTime() : null;

      if (creationTime && creationTime >= startD && creationTime <= endD) {
        fCurrent++;
        
        if (isApproved) {
          aprCurrent++;
          if (data.imovel?.aluguel) {
            const aluguelNum = parseFloat(data.imovel.aluguel.toString().replace(/\./g, '').replace(',', '.'));
            if (!isNaN(aluguelNum)) vAprov += aluguelNum;
          }
        }
        
        if (isEvaluating) {
          if (data.imovel?.aluguel) {
            const aluguelNum = parseFloat(data.imovel.aluguel.toString().replace(/\./g, '').replace(',', '.'));
            if (!isNaN(aluguelNum)) vEmAnalise += aluguelNum;
          }
        }
      }

      if (creationTime && creationTime >= prevStartD && creationTime <= prevEndD) {
        fPrev++;
      }
    });

    // B. MOTOR DE CHURN / FINANCEIRO E SAÚDE DA CARTEIRA
    let pMrr = 0, pVolume = 0, pAtivos = 0, pRisco = 0;

    allContracts.forEach((data) => {
      const isTerminated = data.status === "Contrato Encerrado";
      
      const endedDate = data.dataEncerramentoDefinitivo?.toDate() || (isTerminated ? data.updatedAt?.toDate() || data.dataAtivacao?.toDate() : null);
      const endedTime = endedDate ? endedDate.getTime() : null;

      if (isTerminated && endedTime) {
        if (endedTime >= startD && endedTime <= endD) {
          eCurrent++;
        } else if (endedTime >= prevStartD && endedTime <= prevEndD) {
          ePrev++;
        }
      }

      if (!isTerminated && (data.encerramentoSolicitado === true || data.idEncerramentoSolicitado === true)) {
        ePend++;
      }

      if (!isTerminated) {
        pAtivos++;
        pMrr += parseCurrency(data.faturamento?.valorTaxa);
        pVolume += parseCurrency(data.imovel?.aluguel);
        
        if (data.status === "Cancelada/Suspensa" || data.status === "Inadimplente" || data.ciclo?.statusRepasse === "Erro na NF") {
          pRisco++;
        }
      }
    });

    setPortfolioStats({
      mrr: pMrr,
      volumeGarantido: pVolume,
      totalAtivos: pAtivos,
      contratosEmRisco: pRisco,
      taxaInadimplencia: pAtivos > 0 ? ((pRisco / pAtivos) * 100) : 0
    });

    setPeriodStats({
      fichasCurrent: fCurrent,
      fichasPrev: fPrev,
      encerradosCurrent: eCurrent,
      encerradosPrev: ePrev,
      encerramentoPendente: ePend,
      aprovadasCurrent: aprCurrent,
      volumeAprovado: vAprov,
      volumeEmAnalise: vEmAnalise
    });

  }, [allRequests, allContracts, dateRange]);

  // ============================================================================
  // 3. FUNÇÕES AUXILIARES DE FORMATAÇÃO E TRENDS
  // ============================================================================
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const getTrendData = (current, prev, isNegativeGood = false) => {
    const diff = current - prev;
    if (diff === 0) return { text: "-", color: "text-slate-500 bg-slate-50/50", icon: null };
    
    if (diff > 0) {
      return { 
        text: `+${diff}`, 
        color: isNegativeGood ? "text-red-600 bg-red-50" : "text-green-600 bg-green-50", 
        icon: <ArrowUpRight className="w-3.5 h-3.5 mr-1" /> 
      };
    }
    
    return { 
      text: `${diff}`, 
      color: isNegativeGood ? "text-green-600 bg-green-50" : "text-red-600 bg-red-50", 
      icon: <ArrowDownRight className="w-3.5 h-3.5 mr-1" /> 
    };
  };

  const taxaAprovacao = periodStats.fichasCurrent > 0 
    ? Math.round((periodStats.aprovadasCurrent / periodStats.fichasCurrent) * 100) 
    : 0;

  // ============================================================================
  // 4. MOTOR DE EXPORTAÇÃO (CSV HÍBRIDO)
  // ============================================================================
  const handleExportCSV = () => {
    if (allCompanies.length === 0) return;

    const headers = ["ID", "Nome Imobiliária", "CNPJ", "Data de Credenciamento", "Status"];
    
    const csvRows = [
      headers.join(";"), // Cabeçalho separado por ponto e vírgula
      ...allCompanies.map(emp => {
        const dataFormatada = emp.createdAt.toLocaleDateString('pt-BR');
        // Aspas garantem que vírgulas nos nomes não quebrem as colunas no Excel
        return `"${emp.id}";"${emp.nome}";"${emp.cnpj}";"${dataFormatada}";"${emp.status}"`;
      })
    ];

    const csvString = csvRows.join("\n");
    // \uFEFF força o padrão UTF-8 com BOM para o Excel ler acentos brasileiros perfeitamente
    const blob = new Blob(["\uFEFF" + csvString], { type: "text/csv;charset=utf-8;" });
    
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Relatorio_Rede_Parceiros_${getTodayString()}.csv`);
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full font-sans animate-fade-in bg-[#f8fafc] min-h-full">
      
      {/* HEADER E DATE PICKER */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-6 border-b border-slate-200/60 pb-6">
        
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center shrink-0 border border-teal-100/50 shadow-sm">
            <LayoutDashboard className="w-7 h-7" style={{ color: brandColor }} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">
              Visão Geral
            </h1>
            <p className="text-sm font-medium text-slate-500 mt-1">
              Controle central da operação Real Benefícios.
            </p>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
          <div className="flex items-center gap-2 bg-white border border-slate-200/60 rounded-xl px-3 py-2.5 shadow-sm w-full sm:w-auto hover:border-teal-300 transition-colors">
            <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
            <input 
              type="date" 
              value={dateRange.start}
              max={dateRange.end}
              onChange={(e) => setDateRange(prev => ({...prev, start: e.target.value}))}
              className="text-xs sm:text-sm font-medium text-slate-600 bg-transparent focus:outline-none cursor-pointer"
            />
            <span className="text-slate-300 text-xs sm:text-sm">até</span>
            <input 
              type="date" 
              value={dateRange.end}
              min={dateRange.start}
              onChange={(e) => setDateRange(prev => ({...prev, end: e.target.value}))}
              className="text-xs sm:text-sm font-medium text-slate-600 bg-transparent focus:outline-none cursor-pointer"
            />
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button 
              className="flex-1 sm:flex-none justify-center items-center px-5 py-2.5 bg-white border border-slate-200/80 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm flex"
              onClick={() => window.location.href = '/super-admin/equipe'}
            >
              <Users className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Equipe</span>
            </button>
            <button 
              style={{ backgroundColor: brandColor }}
              className="flex-1 sm:flex-none justify-center items-center px-6 py-2.5 text-white rounded-xl text-sm font-semibold hover:brightness-110 transition-all shadow-sm flex"
              onClick={() => window.location.href = '/super-admin/imobiliarias'}
            >
              <PlusCircle className="w-4 h-4 mr-2" /> Imobiliária
            </button>
          </div>
        </div>
      </div>

      {/* GRID DE CARDS SUPERIORES */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-5 mb-8">
        <StatCard 
          icon={Building2} 
          title="Imobiliárias" 
          value={stats.totalCompanies} 
          bgClass="bg-emerald-50" 
          colorClass="text-emerald-600" 
        />
        <StatCard 
          icon={FileText} 
          title="Fichas (Período)" 
          value={periodStats.fichasCurrent} 
          bgClass="bg-blue-50" 
          colorClass="text-blue-600"
          trend={getTrendData(periodStats.fichasCurrent, periodStats.fichasPrev)}
        />
        <StatCard 
          icon={FileX} 
          title="Encerrados (Período)" 
          value={periodStats.encerradosCurrent} 
          bgClass={periodStats.encerramentoPendente > 0 ? "bg-red-50" : "bg-rose-50"} 
          colorClass={periodStats.encerramentoPendente > 0 ? "text-red-600" : "text-rose-600"}
          trend={getTrendData(periodStats.encerradosCurrent, periodStats.encerradosPrev, true)}
          alert={periodStats.encerramentoPendente > 0 ? `${periodStats.encerramentoPendente} PENDENTE` : null}
        />
        <StatCard 
          icon={ShieldCheck} 
          title="Usuários Ativos" 
          value={stats.totalUsers} 
          bgClass="bg-purple-50" 
          colorClass="text-purple-600" 
        />
        <StatCard 
          icon={Send} 
          title="Convites Env." 
          value={stats.pendingInvites} 
          bgClass="bg-amber-50" 
          colorClass="text-amber-600" 
        />
      </div>

      {/* SAÚDE DA CARTEIRA */}
      <div className="mb-8 animate-fade-in">
        <h2 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-brand-primary" /> Saúde da Carteira Ativa (Estoque Global)
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          <div className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
            <div className="absolute right-4 top-6 p-3 bg-emerald-50 rounded-xl text-emerald-600 group-hover:scale-110 transition-transform">
              <Building2 className="w-6 h-6" />
            </div>
            <p className="text-slate-500 text-sm font-semibold mb-1">Volume Garantido (Risco Protegido)</p>
            <h3 className="text-3xl font-bold text-slate-800 tracking-tight">{formatCurrency(portfolioStats.volumeGarantido)}</h3>
            <p className="text-xs text-slate-400 mt-2 font-medium">Soma de todos os aluguéis sob custódia</p>
          </div>

          <div className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
            <div className="absolute right-4 top-6 p-3 bg-blue-50 rounded-xl text-blue-600 group-hover:scale-110 transition-transform">
              <Wallet className="w-6 h-6" />
            </div>
            <p className="text-slate-500 text-sm font-semibold mb-1">Receita Recorrente (MRR)</p>
            <h3 className="text-3xl font-bold text-slate-800 tracking-tight">{formatCurrency(portfolioStats.mrr)}</h3>
            <p className="text-xs text-slate-400 mt-2 font-medium">Projeção mensal contínua (exclui setup)</p>
          </div>

          <div className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-start mb-1">
                <p className="text-slate-500 text-sm font-semibold">Termômetro de Inadimplência</p>
                <span className={`text-lg font-bold ${portfolioStats.taxaInadimplencia > 5 ? 'text-red-600' : portfolioStats.taxaInadimplencia > 2 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {portfolioStats.taxaInadimplencia.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-slate-100 h-2 rounded-full mt-2 overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-1000 ${portfolioStats.taxaInadimplencia > 5 ? 'bg-red-500' : portfolioStats.taxaInadimplencia > 2 ? 'bg-yellow-500' :'bg-green-500'}`}
                  style={{ width: `${Math.min(portfolioStats.taxaInadimplencia, 100)}%` }}
                ></div>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3 font-medium">
              <strong className="text-slate-600">{portfolioStats.contratosEmRisco}</strong> de <strong className="text-slate-600">{portfolioStats.totalAtivos}</strong> contratos ativos com risco.
            </p>
          </div>
        </div>
      </div>

      {/* GRID INFERIOR */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 h-fit">
          
          <div className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm flex flex-col hover:shadow-md transition-shadow">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-6">
              <div className="p-2 bg-teal-50 rounded-lg border border-teal-100/50"><PieChart className="w-4 h-4" style={{ color: brandColor }} /></div>
              Aprovação no Período
            </h3>
            
            <div className="flex flex-col items-center justify-center flex-1 py-4">
              <div className="relative w-36 h-36 flex items-center justify-center">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                  <path className="text-slate-100" strokeWidth="3.5" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path className="text-teal-500 transition-all duration-1000 ease-out" strokeDasharray={`${taxaAprovacao}, 100`} strokeWidth="3.5" strokeLinecap="round" stroke="currentColor"fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <div className="absolute flex flex-col items-center justify-center text-center">
                  <span className="text-3xl font-bold text-slate-700">{taxaAprovacao}%</span>
                </div>
              </div>
              <p className="text-sm font-medium text-slate-500 mt-6 text-center px-4 leading-relaxed">
                De <strong className="text-slate-700 mx-1">{periodStats.fichasCurrent}</strong> fichas recebidas, <strong className="text-teal-600 mx-1">{periodStats.aprovadasCurrent}</strong> foram garantidas.
              </p>
            </div>
          </div>

          <div className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm flex flex-col hover:shadow-md transition-shadow">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-6">
              <div className="p-2 bg-teal-50 rounded-lg border border-teal-100/50"><TrendingUp className="w-4 h-4" style={{ color: brandColor }} /></div>
              Volume de Aluguéis (Período)
            </h3>
            
            <div className="space-y-6 mt-2 flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-4 bg-green-50/50 p-4 rounded-xl border border-green-100/50">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center shrink-0 shadow-sm">
                  <Wallet className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-green-700/80 mb-1">Garantia Aprovada</p>
                  <p className="text-xl font-semibold text-slate-700">{formatCurrency(periodStats.volumeAprovado)}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 bg-blue-50/50 p-4 rounded-xl border border-blue-100/50">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0 shadow-sm">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-blue-700/80 mb-1">Em Avaliação na Mesa</p>
                  <p className="text-xl font-semibold text-slate-700">{formatCurrency(periodStats.volumeEmAnalise)}</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div className="lg:col-span-1 bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm h-full flex flex-col hover:shadow-md transition-shadow">
          <h3 className="font-semibold text-slate-700 mb-6 flex items-center gap-2">
            <div className="p-2 bg-slate-50 rounded-lg border border-slate-100"><Building2 className="w-4 h-4 text-slate-600" /></div>
            Novos Parceiros
          </h3>
          
          <div className="space-y-6 flex-1 mt-2">
            {recentActivities.length > 0 ? recentActivities.map((act) => (
              <div key={act.id} className="flex items-start gap-4">
                <div className="mt-1.5 w-2 h-2 rounded-full shadow-[0_0_8px_#00a99d]" style={{ backgroundColor: brandColor }}></div>
                <div>
                  <p className="text-sm font-semibold text-slate-700 leading-tight">{act.description}</p>
                  <span className="text-xs font-medium text-slate-500 mt-1 block">
                    Credenciada em {act.time?.toLocaleDateString()}
                  </span>
                </div>
              </div>
            )) : (
              <p className="text-sm text-slate-400 text-center py-10 font-medium bg-slate-50 rounded-xl border border-slate-100">
                Nenhuma imobiliária nova recente.
              </p>
            )}
          </div>
          
          {/* 🟢 O BOTÃO AGORA CHAMA O ESTADO DO SLIDE-OVER */}
          <button 
            onClick={() => setIsSlideOverOpen(true)}
            className="w-full mt-6 py-3.5 border border-slate-200/80 rounded-xl bg-slate-50 text-sm font-medium flex items-center justify-center hover:bg-slate-100 hover:border-teal-300 hover:text-teal-700 transition-all text-slate-600 cursor-pointer"
          >
            Ver Relatório Completo <ExternalLink className="w-3 h-3 ml-2" />
          </button>
        </div>
      </div>

      {/* ============================================================================
          🚀 NOVO: MODAL DA REDE DE PARCEIROS (CENTRALIZADO)
          ============================================================================ */}
      {isSlideOverOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6">
          
          {/* OVERLAY ESCURECIDO */}
          <div 
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setIsSlideOverOpen(false)}
          ></div>

          {/* CONTAINER DO MODAL CENTRALIZADO */}
          <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl flex flex-col animate-fade-in transform transition-all max-h-[90vh]">
            
            {/* CABEÇALHO DO MODAL */}
            <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-white shrink-0 rounded-t-3xl">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-teal-50/50 rounded-xl border border-teal-100/50">
                  <Building2 className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-800 tracking-tight">Rede de Parceiros</h2>
                  <p className="text-[11px] font-medium text-slate-500">{allCompanies.length} imobiliárias cadastradas</p>
                </div>
              </div>
              <button 
                onClick={() => setIsSlideOverOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* LISTA ROLÁVEL (TODAS AS IMOBILIÁRIAS) */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar bg-slate-50/30">
              {allCompanies.map((comp) => (
                <div key={comp.id} className="p-4 border border-slate-200/60 rounded-2xl bg-white hover:border-teal-200 transition-colors shadow-sm flex flex-col">
                  <h4 className="font-semibold text-slate-700 text-sm leading-snug">{comp.nome}</h4>
                  <div className="mt-3 flex items-center justify-between text-[11px] font-medium text-slate-500">
                    <span className="bg-slate-50 px-2 py-1 rounded-md border border-slate-100 text-slate-500">CNPJ: {comp.cnpj}</span>
                    <span>{comp.createdAt.toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* RODAPÉ COM BOTÃO DE DOWNLOAD CSV */}
            <div className="p-6 border-t border-slate-100 shrink-0 bg-white rounded-b-3xl">
              <button 
                onClick={handleExportCSV}
                className="w-full py-3.5 text-white rounded-xl text-sm font-semibold hover:brightness-110 transition-all flex items-center justify-center shadow-md shadow-[#00a99d]/20 cursor-pointer"
                style={{ backgroundColor: brandColor }}
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar Relatório (CSV)
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
    </div>
  );
};

export default AdminDashboard;